import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Button } from "components/common/Button.component";
import { KEY } from "components/common/constants";
import { Menu } from "components/common/menu/Menu.component";
import { MenuItemChosenContext } from "components/common/menu/MenuContext";
import { DropdownPosition } from "types/gaiaComponents/dropdownButton.types";
import { IMenuButton } from "types/gaiaComponents/menu";
import "./menuButton.css";

const POSITION_CLASSES: Record<string, string> = {
    [DropdownPosition.BOTTOM_LEFT]: "menu-button__menu--bottom menu-button__menu--left",
    [DropdownPosition.BOTTOM_RIGHT]: "menu-button__menu--bottom menu-button__menu--right",
    [DropdownPosition.TOP_LEFT]: "menu-button__menu--top menu-button__menu--left",
    [DropdownPosition.TOP_RIGHT]: "menu-button__menu--top menu-button__menu--right",
};

const FOCUS_FIRST = "first";
const FOCUS_LAST = "last";

// A disclosure button: the menu items are ordinary buttons after the trigger, so Tab and Shift+Tab move
// through them in order, and the arrow keys, Home and End move between them too. Clicking an item,
// clicking outside, tabbing out or pressing Escape closes the menu.
export const MenuButton = (props: IMenuButton) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const pendingFocusRef = useRef<typeof FOCUS_FIRST | typeof FOCUS_LAST | null>(null);
    const menuId = useId();

    // The outside-click listener only re-subscribes when the menu opens or closes, so it reads the
    // latest callback from a ref
    const onOpenChangeRef = useRef(props.onOpenChange);
    useEffect(() => {
        onOpenChangeRef.current = props.onOpenChange;
    }, [props.onOpenChange]);

    // Stable: it only uses the state setter and a ref
    const changeOpen = useCallback((nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);
        onOpenChangeRef.current?.(nextIsOpen);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const close = () => {
            setIsOpen(false);
            onOpenChangeRef.current?.(false);
        };

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                close();
            }
        };

        // Escape from inside the wrapper is handled (and stopped) by onKeyDown. This covers focus left
        // outside it, e.g. on the body after Safari, which doesn't focus a clicked button, opened the menu
        const closeOnEscapeOutside = (event: KeyboardEvent) => {
            if (event.key === KEY.ESCAPE && !wrapperRef.current?.contains(event.target as Node)) {
                close();
                triggerRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscapeOutside);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscapeOutside);
        };
    }, [isOpen]);

    // Disabled items are skipped by the arrow keys, as they are by Tab
    const getEnabledItems = () => Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>(".ga-menu__item:not(:disabled)") ?? []
    );

    // Opening from the keyboard focuses an item, but the items only exist after the render that opens the menu
    useEffect(() => {
        if (!isOpen || !pendingFocusRef.current) return;

        const items = getEnabledItems();
        (pendingFocusRef.current === FOCUS_LAST ? items[items.length - 1] : items[0])?.focus();
        pendingFocusRef.current = null;
    }, [isOpen]);

    const moveFocus = (key: string) => {
        const items = getEnabledItems();
        if (items.length === 0) return;

        const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
        let nextIndex: number;
        switch (key) {
            case KEY.HOME:
                nextIndex = 0;
                break;
            case KEY.END:
                nextIndex = items.length - 1;
                break;
            case KEY.ARROW_DOWN:
                // From the trigger (index -1) this lands on the first item; past the last it wraps around
                nextIndex = (currentIndex + 1) % items.length;
                break;
            default:
                nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        }
        items[nextIndex].focus();
    };

    const onKeyDown = (event: KeyboardEvent) => {
        const isArrow = event.key === KEY.ARROW_DOWN || event.key === KEY.ARROW_UP;

        if (!isOpen) {
            // Arrow keys on the closed trigger open the menu at the first (down) or last (up) item
            if (isArrow && event.target === triggerRef.current && !props.disabled) {
                event.preventDefault();
                pendingFocusRef.current = event.key === KEY.ARROW_UP ? FOCUS_LAST : FOCUS_FIRST;
                changeOpen(true);
            }
            return;
        }

        if (event.key === KEY.ESCAPE) {
            event.stopPropagation();
            changeOpen(false);
            triggerRef.current?.focus();
        } else if (isArrow || event.key === KEY.HOME || event.key === KEY.END) {
            // Stop the page from scrolling while moving through the items
            event.preventDefault();
            moveFocus(event.key);
        }
    };

    const onFocusOut = (event: FocusEvent) => {
        // Only a known focus target outside closes it (Tab past the last item, Shift+Tab before the
        // trigger). A null target (a click on a non-focusable area, or Safari not focusing a clicked
        // button) must not close it here, or the menu unmounts before the item's click fires. Clicks
        // outside are handled by the mousedown listener instead.
        const nextFocused = event.relatedTarget as Node | null;
        if (isOpen && nextFocused && !wrapperRef.current?.contains(nextFocused)) {
            changeOpen(false);
        }
    };

    // Native listeners on the wrapper: it's a plain container that only observes the key and focus events
    // of its trigger and items, not an interactive element itself. They subscribe once and call the
    // latest render's handlers through a ref
    const wrapperHandlersRef = useRef({onKeyDown, onFocusOut});
    useEffect(() => {
        wrapperHandlersRef.current = {onKeyDown, onFocusOut};
    });

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const keyDownListener = (event: KeyboardEvent) => wrapperHandlersRef.current.onKeyDown(event);
        const focusOutListener = (event: FocusEvent) => wrapperHandlersRef.current.onFocusOut(event);
        wrapper.addEventListener("keydown", keyDownListener);
        wrapper.addEventListener("focusout", focusOutListener);
        return () => {
            wrapper.removeEventListener("keydown", keyDownListener);
            wrapper.removeEventListener("focusout", focusOutListener);
        };
    }, []);

    // Called by a MenuItem after its own onClick ran; disabled items, the title and separators never call it.
    // Stable, so the context value doesn't change and re-render every item when MenuButton re-renders
    const onItemChosen = useCallback(() => {
        // The focused item is about to unmount, which would drop focus to the body. If the item's action
        // moved focus elsewhere (e.g. into a dialog), keep that
        if (menuRef.current?.contains(document.activeElement)) {
            triggerRef.current?.focus();
        }
        changeOpen(false);
    }, [changeOpen]);

    const positionClass = POSITION_CLASSES[props.position ?? DropdownPosition.BOTTOM_LEFT]
        ?? POSITION_CLASSES[DropdownPosition.BOTTOM_LEFT];

    return (
        <div ref={wrapperRef}
             className={["menu-button", props.className].filter(Boolean).join(" ")}>
            <Button ref={triggerRef}
                    id={props.id}
                    type={props.buttonType}
                    text={props.text}
                    icon={props.icon}
                    iconOnly={!props.text}
                    title={props.title}
                    ariaLabel={props.ariaLabel}
                    ariaExpanded={isOpen}
                    ariaControls={isOpen ? menuId : undefined}
                    disabled={props.disabled}
                    dataCy={props.dataCy}
                    onClick={() => changeOpen(!isOpen)}/>
            {isOpen && (
                <div id={menuId}
                     ref={menuRef}
                     className={`menu-button__menu ${positionClass}`}>
                    <MenuItemChosenContext.Provider value={onItemChosen}>
                        <Menu title={props.menuTitle}
                              className={props.menuClassName}>
                            {props.children}
                        </Menu>
                    </MenuItemChosenContext.Provider>
                </div>
            )}
        </div>
    );
}
